import React, { useState, useRef, useEffect } from 'react';
import { Search, ChevronDown, X } from 'lucide-react';

/**
 * Searchable Dropdown Component
 * A custom dropdown with built-in search functionality
 * Search bar appears inside the dropdown when opened
 */
const SearchableDropdown = ({ 
    options = [], 
    value, 
    onChange, 
    placeholder = "Select Item...",
    searchPlaceholder = "Search product...",
    getOptionLabel = (option) => option.label,
    getOptionValue = (option) => option.value,
    noResultsText = "No results found",
    className = ""
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef(null);
    const searchInputRef = useRef(null);

    // Filter options based on search term
    const filteredOptions = options.filter(option => 
        getOptionLabel(option).toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Get selected option
    const selectedOption = options.find(option => getOptionValue(option) === value);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Focus search input when dropdown opens
    useEffect(() => {
        if (isOpen && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [isOpen]);

    const handleSelect = (option) => {
        onChange(getOptionValue(option));
        setIsOpen(false);
        setSearchTerm('');
    };

    const handleToggle = () => {
        setIsOpen(!isOpen);
        if (isOpen) {
            setSearchTerm('');
        }
    };

    return (
        <div className={`relative ${className}`} ref={dropdownRef}>
            {/* Dropdown Trigger Button */}
            <button
                type="button"
                onClick={handleToggle}
                className="w-full px-4 py-3 pr-10 rounded-xl border border-slate-200 bg-white hover:border-emerald-400 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all text-left font-medium text-slate-700"
            >
                <span className={selectedOption ? 'text-slate-700' : 'text-slate-400'}>
                    {selectedOption ? getOptionLabel(selectedOption) : placeholder}
                </span>
                <ChevronDown 
                    className={`absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
                    size={16} 
                />
            </button>

            {/* Dropdown Menu */}
            {isOpen && (
                <div className="absolute z-50 w-full mt-2 bg-white rounded-xl border border-slate-200 shadow-xl max-h-96 overflow-hidden">
                    {/* Search Input Inside Dropdown */}
                    <div className="p-3 border-b border-slate-100 bg-slate-50">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                            <input
                                ref={searchInputRef}
                                type="text"
                                placeholder={searchPlaceholder}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-9 pr-8 py-2 rounded-lg border border-slate-200 bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all text-sm text-slate-700"
                                onClick={(e) => e.stopPropagation()}
                            />
                            {searchTerm && (
                                <button
                                    type="button"
                                    onClick={() => setSearchTerm('')}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-100 rounded-full transition-colors"
                                >
                                    <X size={14} className="text-slate-400" />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Options List */}
                    <div className="overflow-y-auto max-h-80">
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((option, index) => {
                                const optionValue = getOptionValue(option);
                                const isSelected = optionValue === value;
                                
                                return (
                                    <button
                                        key={optionValue || index}
                                        type="button"
                                        onClick={() => handleSelect(option)}
                                        className={`w-full px-4 py-3 text-left hover:bg-emerald-50 transition-colors border-b border-slate-50 last:border-b-0 ${
                                            isSelected ? 'bg-emerald-50 text-emerald-700 font-semibold' : 'text-slate-700'
                                        }`}
                                    >
                                        <div className="text-sm font-medium">
                                            {getOptionLabel(option)}
                                        </div>
                                    </button>
                                );
                            })
                        ) : (
                            <div className="px-4 py-8 text-center text-slate-400 text-sm">
                                <Search size={32} className="mx-auto mb-2 text-slate-300" />
                                <p>{noResultsText}</p>
                                {searchTerm && (
                                    <p className="text-xs mt-1">untuk "{searchTerm}"</p>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SearchableDropdown;
